import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { setAutoRenew, setPaperless } from 'actions/expiryRenewalActions';
import ToggleControl from 'common/ToggleControl';
import { RENEWAL, RENEWAL_COMBINED_PAYMENTS, RENEWAL_DIRECTDEBIT, RENEWAL_PAYMENT, RENEWAL_PAYMENT_OPTIONS } from 'constants/routeConstants';
import useCosmosTrackEvent from 'hooks/useCosmosTrackEvent';
import Button from 'shared/components/Button/Button';
import { dispatch } from 'store';
import { Store } from 'types/store';
import Footer from 'views/MyRac/ExpiryRenewal/components/atoms/Footer';

import Banner from './components/molecules/Banner';
import BasketOptions from './components/molecules/BasketOptions';
import Confirmation from './components/molecules/Confirmation';
import Stepper from './components/organisms/Stepper';

const RenewalSummary: React.FC = () => {
  const { monthlyFlex, pageModel, toggles } = useSelector((state: Store) => state.expiryRenewal);
  const { custInfo } = useSelector((state: Store) => state.customer);

  const navigate = useNavigate();

  const { cosmosTrackEvent } = useCosmosTrackEvent('ExpiryRenewals');

  const paymentRedirect = (): string => {
    if (pageModel?.hasPreviousCardDetails && !monthlyFlex) return RENEWAL_PAYMENT_OPTIONS;
    if (pageModel?.isEligibleForMonthlyFlex && monthlyFlex) return RENEWAL_COMBINED_PAYMENTS;
    if (pageModel?.paymentMethod === 'DirectDebit') return RENEWAL_DIRECTDEBIT;
    return RENEWAL_PAYMENT;
  };

  useEffect(() => {
    // Send monthly active user data to cosmos db
    cosmosTrackEvent('renewal_journey_summary');
  }, [cosmosTrackEvent]);

  useEffect(() => {
    if (!pageModel) navigate(RENEWAL, { replace: true });
  }, [pageModel, navigate]);

  if (!pageModel) return null;

  return (
    <>
      {pageModel.discountModel?.isDiscountedPrice && (
        <Banner />
      )}
      <Stepper step={3} />
      <div className="p-3 fs-6">
        <div className="card border rounded mt-3 mb-4">
          {pageModel.isEligibleForMonthlyFlex && monthlyFlex ? (
            <div className="card-body pb-0" style={{ backgroundColor: '#EBF0F3' }}>
              <h5>Relax, with monthly payments</h5>
              <p>When you pay monthly, you don&apos;t have to worry about renewing your cover. You can cancel anytime.</p>
            </div>
          ) : (
            <div className="card-body pb-0">
              <div className="float-right">
                <ToggleControl
                  onColor="#2a976f"
                  onToggleChange={() => dispatch(setAutoRenew(!toggles?.autoRenew))}
                  checked={toggles?.autoRenew ?? pageModel.autoRenewToggle?.isEnabled}
                />
              </div>
              <h5>Enable auto renew</h5>
              <p>Keep things hassle free. Choose to auto renew for the peace of mind you&apos;re always covered. Don&apos;t worry, we&apos;ll send you a reminder before your renewal and you can change your mind at any time.</p>
            </div>
          )}
        </div>
        {pageModel.goPaperlessToggle?.isEnabled && !custInfo?.isPaperLess && (
          <div className="card border rounded my-4">
            <div className="card-body pb-0">
              <div className="float-right">
                <ToggleControl
                  onColor="#2a976f"
                  onToggleChange={() => dispatch(setPaperless(!toggles?.paperless))}
                  checked={toggles?.paperless}
                />
              </div>
              <h5>Go paperless</h5>
              <p>You&apos;ll get emails instead of post to reduce paper waste. Your documents will be ready in myRAC.</p>
            </div>
          </div>
        )}
        <Confirmation termsConditions={pageModel.documents?.termsConditions} />
        <div className="card border rounded mt-3 mb-4">
          <div className="card-body pb-0">
            <BasketOptions />
          </div>
        </div>
        <Button
          onClickHandler={() => navigate(paymentRedirect())}
          buttonText="Renew Now"
        />
      </div>
      <Footer />
    </>
  );
};

export default RenewalSummary;
